import { useState } from "react";
import { Link } from "react-router-dom";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Wand2, Loader2, Type, AlertCircle, Coins } from "lucide-react";

export default function LyricsEditor({ 
  lyrics, 
  setLyrics, 
  audioFile,
  userCredits = 0,
  onExtractLyrics,
  aiCost = 400
}) {
  const [isExtracting, setIsExtracting] = useState(false);
  const [error, setError] = useState(null);
  
  const canAffordAI = userCredits >= aiCost;
  const costDisplay = `${aiCost} credits ($${(aiCost / 100).toFixed(2)})`;

  const handleAIExtract = async () => {
    if (!audioFile) {
      setError("Please upload an audio file first");
      return;
    }
    
    if (!canAffordAI) {
      setError(`You need ${aiCost} credits for AI extraction. You have ${userCredits}.`);
      return;
    }

    setIsExtracting(true);
    setError(null);
    
    try {
      if (onExtractLyrics) {
        const extractedLyrics = await onExtractLyrics(audioFile);
        if (extractedLyrics) {
          setLyrics(extractedLyrics);
        }
      }
    } catch (err) {
      setError(err.message || "Failed to extract lyrics");
    } finally {
      setIsExtracting(false);
    }
  };

  const lineCount = lyrics ? lyrics.split('\n').filter(l => l.trim()).length : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#1a1a1a] border border-[#2a2a2a] mb-4">
          <Type className="w-8 h-8 text-purple-400" />
        </div>
        <h2 className="text-2xl font-bold text-[#e0e0e0] mb-2">Add Your Lyrics</h2>
        <p className="text-[#808080]">
          Enter lyrics manually or use AI to extract them from your audio
        </p>
      </div>

      {/* AI Extraction Option */}
      <div className="bg-gradient-to-r from-purple-900/20 to-pink-900/20 border border-purple-500/30 rounded-xl p-6">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-purple-500/20 rounded-lg">
            <Wand2 className="w-6 h-6 text-purple-400" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-[#e0e0e0] mb-1">
              AI Lyrics Extraction
            </h3>
            <p className="text-sm text-[#909090] mb-4">
              Let AI automatically transcribe lyrics from your audio track. 
              Cost: <span className="text-purple-400 font-medium">{costDisplay}</span>
            </p>
            <div className="flex items-center gap-3 flex-wrap">
              <Button
                onClick={handleAIExtract}
                disabled={!audioFile || isExtracting || !canAffordAI}
                className="bg-purple-600 hover:bg-purple-700 text-white"
              >
                {isExtracting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Extracting...
                  </>
                ) : (
                  <>
                    <Wand2 className="w-4 h-4 mr-2" />
                    Extract with AI
                  </>
                )}
              </Button>
              <div className="flex items-center gap-1.5 text-sm text-[#606060]">
                <Coins className="w-4 h-4 text-yellow-400" />
                <span>Your balance: <span className={canAffordAI ? "text-green-400" : "text-red-400"}>{userCredits} credits</span></span>
              </div>
            </div>
            {!audioFile && (
              <p className="text-xs text-amber-400 mt-3">
                Upload audio first to use AI extraction
              </p>
            )}
            {audioFile && !canAffordAI && (
              <p className="text-xs text-amber-400 mt-3">
                Insufficient credits. <Link to="/shop" className="text-purple-400 hover:underline">Buy more credits</Link>
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="flex items-center gap-2 p-4 bg-red-900/20 border border-red-500/30 rounded-lg text-red-400">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      {/* Manual Input */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium text-[#a0a0a0]">
            Or enter lyrics manually
          </label>
          {lineCount > 0 && (
            <span className="text-xs text-[#606060]">
              {lineCount} line{lineCount !== 1 ? 's' : ''}
            </span>
          )}
        </div>
        <Textarea
          value={lyrics}
          onChange={(e) => setLyrics(e.target.value)}
          placeholder="Enter your lyrics here...

Each line will appear as a separate lyric card.
Leave blank lines to separate verses.

Example:
Verse 1 line 1
Verse 1 line 2

Verse 2 line 1
Verse 2 line 2"
          className="min-h-[300px] bg-[#0a0a0a] border-[#2a2a2a] text-[#e0e0e0] placeholder:text-[#404040] font-mono text-sm resize-none focus:border-purple-500/50"
        />
      </div>

      {/* Tips */}
      <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg p-4">
        <h4 className="text-sm font-medium text-[#a0a0a0] mb-2">Tips:</h4>
        <ul className="text-xs text-[#606060] space-y-1">
          <li>• Each line becomes one lyric card in the video</li>
          <li>• Blank lines create natural pauses between verses</li>
          <li>• Keep lines short for better readability</li>
          <li>• The timing is automatically calculated based on audio duration</li>
        </ul>
      </div>
    </div>
  );
}
